import type { PublicInvoice, PublicStatement } from '@morales/shared';
import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { DecoRule, OakMark, Wordmark } from '../components/brand';
import { ErrorBox } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { LocaleProvider, LocaleToggle, useLocale } from '../i18n';
import { qk } from '../queryClient';
import { InvoicePaper } from '../components/InvoicePaper';

/**
 * What a customer opens from the emailed link (decision 93), without signing in: the invoice as printed (letterhead,
 * lines, totals, the PACA notice) or the statement (balance, aging, open invoices), with a Print button.
 */
export function PublicDocumentPage() {
  const { token = '' } = useParams();
  // fetched once: every fetch counts as an open
  const doc = useQuery({ queryKey: qk.publicDocument(token), queryFn: ({ signal }) => api.publicDocument(token, signal), retry: false, staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false });
  // the page opens in the customer's language, for this page only; the toggle still switches it
  const language = doc.data?.language;
  return (
    <LocaleProvider key={language ?? 'none'} {...(language ? { initialLocale: language } : {})} persist={false}>
      <PublicDocument doc={doc} token={token} />
    </LocaleProvider>
  );
}

function PublicDocument({ doc, token }: { doc: UseQueryResult<PublicInvoice | PublicStatement>; token: string }) {
  const { t, intlLocale } = useLocale();
  // Pay: the app makes (or reuses) the Square link for the balance and the customer goes there (decision 94)
  const pay = useMutation({ mutationFn: () => api.publicPay(token), onSuccess: (link) => window.location.assign(link.url) });
  const money = (v: number) => formatMoney(v, intlLocale);
  const d = doc.data;

  return (
    <main className="public-doc stack" data-testid="public-doc">
      <div className="public-tools no-print">
        <LocaleToggle />
        {d ? (
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            {t('print')}
          </button>
        ) : null}
      </div>
      {d?.kind === 'invoice' ? null : (
        <header className="letterhead">
          <OakMark />
          <Wordmark />
          <DecoRule />
        </header>
      )}
      {doc.isPending ? <p className="muted">{t('loading')}</p> : null}
      {doc.isError ? <ErrorBox error={doc.error} /> : null}
      {d?.kind === 'invoice' ? (
        <InvoiceSheet d={d}>
          {d.payOnline ? (
            <div className="stack-sm no-print pay-online">
              <button type="button" className="btn btn-primary btn-lg btn-block" disabled={pay.isPending || pay.isSuccess} onClick={() => pay.mutate()}>
                {pay.isPending || pay.isSuccess ? t('payOpening') : t('payOnline').replace('{amount}', money(d.invoice.balance ?? d.invoice.total))}
              </button>
              <p className="muted center">{t('payOnlineHint')}</p>
              {pay.error ? <ErrorBox error={pay.error} /> : null}
            </div>
          ) : d.payPending ? (
            <p className="note center no-print" role="status">
              {t('payPending')}
            </p>
          ) : null}
        </InvoiceSheet>
      ) : d?.kind === 'statement' ? (
        <>
          <section className="card">
            <h1>{t('statement')}</h1>
            <span className="row-title big">{d.customerName}</span>
            <p className="muted">{t('asOf').replace('{date}', formatDate(d.asOf, intlLocale, 'date'))}</p>
            {d.balance !== null ? (
              <p>
                <span className="label">{t('balanceDue')}</span> <span className="money">{money(d.balance)}</span>
              </p>
            ) : null}
            {d.aging ? (
              <dl className="facts plain">
                {(['current', 'd1to30', 'd31to60', 'd61to90', 'd90plus'] as const).map((b) => (
                  <div key={b} className="fact">
                    <dt>{t(`bucket_${b}`)}</dt>
                    <dd>{money(d.aging![b])}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </section>
          <table className="doc-lines">
            <thead>
              <tr>
                <th>{t('invoice')}</th>
                <th className="hide-narrow">{t('invoiceDate')}</th>
                <th>{t('dueDate')}</th>
                <th className="al-right hide-narrow">{t('total')}</th>
                <th className="al-right">{t('balanceDue')}</th>
              </tr>
            </thead>
            <tbody>
              {d.opening !== 0 ? (
                <tr>
                  <td colSpan={4}>{t('openingCol')}</td>
                  <td className="al-right">{money(d.opening)}</td>
                </tr>
              ) : null}
              {d.openInvoices.map((o) => (
                <tr key={o.id} className={o.daysLate > 0 ? 'late' : undefined}>
                  <td>{o.invoiceNo}</td>
                  <td className="hide-narrow">{formatDate(o.date, intlLocale, 'date')}</td>
                  <td>{o.dueDate ? formatDate(o.dueDate, intlLocale, 'date') : '—'}</td>
                  <td className="al-right hide-narrow">{money(o.total)}</td>
                  <td className="al-right">{money(o.balance)}</td>
                </tr>
              ))}
              {d.onAccount !== 0 ? (
                <tr>
                  <td colSpan={4}>{t('moneyOnAccount')}</td>
                  <td className="al-right">{money(d.onAccount)}</td>
                </tr>
              ) : null}
              {d.balance !== null ? (
                <tr className="total-row">
                  <td colSpan={4}>
                    <strong>{t('balanceDue')}</strong>
                  </td>
                  <td className="al-right">
                    <strong>{money(d.balance)}</strong>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          <p className="note center">{t('thankYou')}</p>
        </>
      ) : null}
    </main>
  );
}

/** The invoice as printed, for the emailed link and the portal: the same sheet as the office prints, with the letterhead. */
export function InvoiceSheet({ d, children }: { d: PublicInvoice; children?: ReactNode }) {
  return (
    <InvoicePaper inv={d.invoice} paca={d.paca} company={d.company}>
      {children}
    </InvoicePaper>
  );
}
